import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { COMPANY, PRESENCE } from "@/data/company";
import BankingPartners from "@/components/BankingPartners";

export default function About() {
  return (
    <div data-testid="page-about">
      {/* HEADER */}
      <section className="pt-28 lg:pt-36 pb-20 lg:pb-28 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">About the firm</div>
            <h1
              data-testid="about-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              Patient capital,
              <br />
              institutional discipline.
            </h1>
          </div>
          <div className="lg:col-span-7">
            <p className="text-slate-700 text-lg leading-relaxed">
              Finovest Holdings Ltd is a privately-held investment group
              registered in the British Virgin Islands and operating from
              Singapore. The firm allocates capital with conviction across
              fintech, healthcare, agriculture, real estate development, and
              clean energy — backing operators who build durable businesses
              across cycles.
            </p>
            <p className="mt-6 text-slate-500 leading-relaxed">
              Our approach is principal-led. We commit our own balance sheet,
              partner closely with management teams, and remain disciplined on
              valuation, structure, and downside.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 lg:py-28 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="overline">Operating Principles</div>
          <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Four principles that shape every commitment.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {[
              {
                n: "01",
                t: "Principal capital",
                d: "We invest our own balance sheet — aligning incentives with operators and co-investors.",
              },
              {
                n: "02",
                t: "Long horizons",
                d: "Holding periods measured in cycles, not quarters. Compounding rewards conviction.",
              },
              {
                n: "03",
                t: "Operator partnership",
                d: "We back management teams with autonomy, governance, and a board that adds value.",
              },
              {
                n: "04",
                t: "Disciplined structure",
                d: "Bespoke capital structures that protect downside while preserving meaningful upside.",
              },
            ].map((p) => (
              <div
                key={p.n}
                data-testid={`principle-${p.n}`}
                className="bg-white p-8 lg:p-10 hover:bg-slate-50 transition-colors"
              >
                <div className="overline text-slate-400">{p.n}</div>
                <h3 className="mt-4 font-serif text-2xl tracking-tight text-slate-900">
                  {p.t}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION & OFFICES */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Corporate Identity</div>
            <h2 className="mt-5 font-serif text-3xl lg:text-4xl tracking-tight text-slate-900 leading-tight">
              Registered in the British Virgin Islands.
              <br />
              Operating from Singapore.
            </h2>
            <p className="mt-6 text-slate-500 leading-relaxed max-w-md">
              {COMPANY.registration}. We maintain an active operating presence
              in Singapore alongside our registered office in the BVI.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-8">
              <div className="overline">
                {COMPANY.addresses.bvi.label}
              </div>
              <p className="mt-4 text-slate-900 font-serif text-xl leading-snug">
                {COMPANY.addresses.bvi.line}
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="overline">
                {COMPANY.addresses.singapore.label}
              </div>
              <p className="mt-4 text-slate-900 font-serif text-xl leading-snug">
                {COMPANY.addresses.singapore.line}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRESENCE STRIP */}
      <section className="py-16 lg:py-20 bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="overline text-slate-400">Global Reach</div>
          <h3 className="mt-4 font-serif text-2xl lg:text-3xl text-white">
            Active across {PRESENCE.length} geographies.
          </h3>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
            {PRESENCE.map((p) => (
              <div
                key={p.country}
                className="text-sm text-slate-300 tracking-wide"
                data-testid={`about-presence-${p.country.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="mr-2">{p.flag}</span>
                {p.country}
              </div>
            ))}
          </div>
        </div>
      </section>

      <BankingPartners />

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <div className="overline">Next step</div>
          <h2 className="mt-6 font-serif text-3xl lg:text-4xl tracking-tight text-slate-900">
            Learn more about our portfolio and team.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/portfolio"
              data-testid="about-cta-portfolio"
              className="text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-6 py-3.5 hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
            >
              View portfolio <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/team"
              data-testid="about-cta-team"
              className="text-[12px] tracking-[0.2em] uppercase font-semibold border border-slate-900 text-slate-900 px-6 py-3.5 hover:bg-slate-900 hover:text-white transition-colors"
            >
              Meet the team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
